import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, Cpu, Search, Palette, Megaphone, Heart, DollarSign,
  TrendingUp, TrendingDown, Users, Zap, CheckCircle, AlertTriangle,
  ArrowRight, BarChart3
} from 'lucide-react';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const KOMPETENZ_AREAS = [
  { key: 'strategie', icon: Target, label: 'Strategie & Audit', color: 'from-slate-50 to-slate-100 border-slate-200', iconColor: 'text-slate-700 bg-slate-100', tools: 'AppTweak, Sensor Tower, Google Trends', kpi: 'Product-Market-Fit' },
  { key: 'tech', icon: Cpu, label: 'Tech & Tracking', color: 'from-sky-50 to-sky-100 border-sky-200', iconColor: 'text-sky-700 bg-sky-100', tools: 'AppsFlyer, Adjust, RevenueCat, Firebase', kpi: 'Daten-Genauigkeit' },
  { key: 'aso', icon: Search, label: 'ASO (Organisch)', color: 'from-teal-50 to-teal-100 border-teal-200', iconColor: 'text-teal-700 bg-teal-100', tools: 'App Store Connect, Play Console, AppTweak', kpi: 'Conversion Rate (Store)' },
  { key: 'creative', icon: Palette, label: 'Creative Factory', color: 'from-amber-50 to-amber-100 border-amber-200', iconColor: 'text-amber-700 bg-amber-100', tools: 'Foreplay.co, CapCut, Playable Factory', kpi: 'CTR (Click-Through-Rate)' },
  { key: 'media', icon: Megaphone, label: 'Media Buying', color: 'from-indigo-50 to-indigo-100 border-indigo-200', iconColor: 'text-indigo-700 bg-indigo-100', tools: 'Smartly.io, TikTok Creative Center', kpi: 'CPI (Cost per Install)' },
  { key: 'retention', icon: Heart, label: 'Retention & CRM', color: 'from-rose-50 to-rose-100 border-rose-200', iconColor: 'text-rose-700 bg-rose-100', tools: 'Mixpanel, Braze, Clevertap', kpi: 'LTV (Lifetime Value)' },
  { key: 'finance', icon: DollarSign, label: 'Sales & Finance', color: 'from-emerald-50 to-emerald-100 border-emerald-200', iconColor: 'text-emerald-700 bg-emerald-100', tools: 'Pipedrive, HubSpot, Loom', kpi: 'Profit-Margin' },
];

export default function KompetenzHubPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ppiForm, setPpiForm] = useState({ ppi_price: 5.00, actual_cpi: 3.50, monthly_volume: 10000, media_spend_pct: 0.70 });
  const [ppiResult, setPpiResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/kompetenz/overview`);
        setData(res.data);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    load();
  }, []);

  const calculatePPI = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/kompetenz/ppi-calculator`, ppiForm);
      setPpiResult(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { calculatePPI(); }, [ppiForm]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm text-slate-500">Kompetenz-Hub wird geladen...</div></div>;

  const getKpiValue = (area) => {
    if (!data) return { value: '-', delta: null };
    switch (area.key) {
      case 'strategie': return { value: `${data.strategie.completion_rate}%`, sub: `${data.strategie.audits_complete}/${data.strategie.total_clients} Audits` };
      case 'tech': return { value: `${data.tech.data_accuracy}%`, sub: `${data.tech.sdk_integrated} SDKs | ${data.tech.skan_configured} SKAN` };
      case 'aso': return { value: `${data.aso.avg_score}/100`, sub: `${data.aso.clients_optimized} Clients optimiert` };
      case 'creative': return { value: data.creative.active, sub: `${data.creative.total} gesamt | ${data.creative.active} aktiv` };
      case 'media': return { value: `€${data.media.avg_cpi}`, sub: `${data.media.total_installs.toLocaleString('de-DE')} Installs` };
      case 'retention': return { value: `${data.retention.avg_d1}%`, sub: `D7: ${data.retention.avg_d7}% | LTV: €${data.retention.avg_ltv_90d}` };
      case 'finance': return { value: `${data.finance.margin_pct}%`, sub: `€${data.finance.margin.toLocaleString('de-DE')} Marge` };
      default: return { value: '-', sub: '' };
    }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.04 } } }}>
      <PageHeader title="Kompetenz-Hub" description="Alle 7 Kompetenz-Säulen im Überblick – Master-Kompetenz: Arbitrage-Management" />

      {/* Arbitrage Monitor */}
      {data?.arbitrage && (
        <motion.div variants={fadeIn} className="rounded-xl border-2 border-slate-900 bg-slate-900 text-white p-5 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold">Arbitrage-Monitor</h3>
              <p className="text-xs text-slate-400">Master-KPI: Was kostet ein Nutzer vs. was bringt er?</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Kosten / Nutzer</p>
              <p className="font-display text-xl font-bold text-red-400">€{data.arbitrage.cost_per_user}</p>
            </div>
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Revenue / Nutzer</p>
              <p className="font-display text-xl font-bold text-emerald-400">€{data.arbitrage.revenue_per_user}</p>
            </div>
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Marge / Nutzer</p>
              <p className={`font-display text-xl font-bold ${data.arbitrage.margin_per_user >= 0 ? 'text-teal-400' : 'text-red-400'}`}>€{data.arbitrage.margin_per_user}</p>
            </div>
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">ROAS</p>
              <p className="font-display text-xl font-bold text-teal-400">{data.arbitrage.roas}x</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* 7 Kompetenz Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {KOMPETENZ_AREAS.map((area) => {
          const Icon = area.icon;
          const kpi = getKpiValue(area);
          return (
            <motion.div key={area.key} variants={fadeIn} className={`kpi-card-hover rounded-xl border bg-gradient-to-br ${area.color} p-5`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg ${area.iconColor} flex items-center justify-center`}>
                  <Icon className="w-4.5 h-4.5" />
                </div>
                <h3 className="font-display text-sm font-bold text-slate-900">{area.label}</h3>
              </div>
              <div className="mb-2">
                <p className="font-display text-2xl font-bold text-slate-900 tabular-nums">{kpi.value}</p>
                <p className="text-[11px] text-slate-600 mt-0.5">{kpi.sub}</p>
              </div>
              <div className="pt-2 border-t border-slate-200/50">
                <p className="text-[10px] text-slate-500"><span className="font-medium">KPI:</span> {area.kpi}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{area.tools}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* PPI Calculator */}
      <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-base font-semibold text-slate-900 mb-1">PPI Arbitrage-Rechner</h3>
        <p className="text-xs text-slate-500 mb-4">Berechne die Marge zwischen Einkaufspreis (CPI) und Verkaufspreis (PPI)</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">PPI-Preis (€/Install)</label>
                <input type="number" step="0.1" value={ppiForm.ppi_price} onChange={e => setPpiForm({...ppiForm, ppi_price: parseFloat(e.target.value) || 0})} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="ppi-price-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Tatsächlicher CPI (€)</label>
                <input type="number" step="0.1" value={ppiForm.actual_cpi} onChange={e => setPpiForm({...ppiForm, actual_cpi: parseFloat(e.target.value) || 0})} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="actual-cpi-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Monatliches Volumen</label>
                <input type="number" value={ppiForm.monthly_volume} onChange={e => setPpiForm({...ppiForm, monthly_volume: parseInt(e.target.value) || 0})} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="monthly-volume-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Media-Spend Anteil (%)</label>
                <input type="number" step="0.01" value={(ppiForm.media_spend_pct * 100).toFixed(0)} onChange={e => setPpiForm({...ppiForm, media_spend_pct: (parseFloat(e.target.value) || 0) / 100})} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            </div>
          </div>
          {ppiResult && (
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 ${ppiResult.profitable ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
                <p className="text-[10px] font-medium text-slate-500 uppercase">Marge / Install</p>
                <p className={`font-display text-xl font-bold ${ppiResult.profitable ? 'text-emerald-700' : 'text-red-700'}`}>€{ppiResult.margin_per_install}</p>
                <p className="text-[10px] text-slate-500">{ppiResult.margin_pct}% Marge</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-[10px] font-medium text-slate-500 uppercase">Monatl. Marge</p>
                <p className="font-display text-xl font-bold text-slate-900">€{ppiResult.total_margin?.toLocaleString('de-DE')}</p>
                <p className="text-[10px] text-slate-500">Jährlich: €{ppiResult.annual_projection?.toLocaleString('de-DE')}</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-[10px] font-medium text-slate-500 uppercase">Brutto-Revenue</p>
                <p className="font-display text-lg font-bold text-slate-900">€{ppiResult.gross_revenue?.toLocaleString('de-DE')}</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-[10px] font-medium text-slate-500 uppercase">Breakeven CPI</p>
                <p className="font-display text-lg font-bold text-slate-900">€{ppiResult.breakeven_cpi}</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
